import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Navigate } from 'react-router-dom'
import axios from 'axios'
import { formatDateTime } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Plus, Users, Trash2, Loader2, Shield, ShieldCheck, UserPlus, Settings, LayoutDashboard, FileText, Gavel, Stethoscope, Fuel, ClipboardList, TrendingUp } from 'lucide-react'
import { toast } from 'sonner'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Lista de módulos disponíveis com ícones e labels
const MODULOS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'processos', label: 'Processos', icon: FileText },
  { id: 'licitacoes', label: 'Licitações', icon: Gavel },
  { id: 'especialistas', label: 'Especialistas', icon: Stethoscope },
  { id: 'abastecimento', label: 'Abastecimento', icon: Fuel },
  { id: 'atendimentos', label: 'Atendimentos', icon: ClipboardList },
  { id: 'indicadores', label: 'Indicadores', icon: TrendingUp },
]

export default function UsuariosPage() {
  const { user, isAdmin } = useAuth()
  const [usuarios, setUsuarios] = useState([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [permissoesDialogOpen, setPermissoesDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedPermissoes, setSelectedPermissoes] = useState([])
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    cargo: '',
    permissoes: []
  })

  const fetchUsuarios = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/users`)
      setUsuarios(response.data)
    } catch (error) {
      toast.error('Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isAdmin) {
      fetchUsuarios()
    }
  }, [isAdmin, fetchUsuarios])

  // Se não for admin, redireciona para dashboard
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await axios.post(`${API}/auth/admin/register`, formData)
      toast.success('Usuário cadastrado com sucesso!')
      setDialogOpen(false)
      setFormData({ nome: '', email: '', senha: '', cargo: '', permissoes: [] })
      fetchUsuarios()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar usuário')
    }
  }

  const handleDelete = async (id, email) => {
    if (email.toLowerCase() === 'jonhnyssabino@gmail.com') {
      toast.error('Não é possível excluir o administrador principal')
      return
    }
    if (!window.confirm(`Tem certeza que deseja excluir o usuário ${email}?`)) return
    try {
      await axios.delete(`${API}/auth/users/${id}`)
      toast.success('Usuário excluído com sucesso!')
      fetchUsuarios()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir usuário')
    }
  }

  const handleEditPermissoes = (usuario) => {
    setSelectedUser(usuario)
    setSelectedPermissoes(usuario.permissoes || [])
    setPermissoesDialogOpen(true)
  }

  const handleSavePermissoes = async () => {
    try {
      await axios.put(`${API}/auth/users/${selectedUser.id}/permissoes`, selectedPermissoes)
      toast.success('Permissões atualizadas com sucesso!')
      setPermissoesDialogOpen(false)
      fetchUsuarios()
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar permissões')
    }
  }

  const selectAllPermissoes = () => {
    setFormData(prev => ({
      ...prev,
      permissoes: MODULOS.map(m => m.id)
    }))
  }

  const clearAllPermissoes = () => {
    setFormData(prev => ({
      ...prev,
      permissoes: []
    }))
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Usuários</h1>
          <p className="text-slate-500 mt-1">Gerencie os usuários do sistema</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="novo-usuario-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Novo Usuário
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Cadastrar Novo Usuário</DialogTitle>
              <DialogDescription>
                Preencha os dados do novo usuário e defina suas permissões
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome Completo</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                    data-testid="input-nome"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cargo">Cargo</Label>
                  <Input
                    id="cargo"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                    required
                    placeholder="Ex: Coordenador"
                    data-testid="input-cargo"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="senha">Senha</Label>
                <Input
                  id="senha"
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  required
                  placeholder="Mínimo 6 caracteres"
                  data-testid="input-senha"
                />
              </div>
              
              {/* Permissões */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Permissões de Acesso</Label>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={selectAllPermissoes}>
                      Selecionar todos
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={clearAllPermissoes}>
                      Limpar
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg border">
                  {MODULOS.map((modulo) => {
                    const Icon = modulo.icon
                    const isChecked = formData.permissoes.includes(modulo.id)
                    return (
                      <label 
                        key={modulo.id} 
                        htmlFor={`permissao-new-${modulo.id}`}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                          isChecked ? 'bg-teal-50 border-teal-300' : 'bg-white border-slate-200 hover:bg-slate-100'
                        }`}
                        data-testid={`permissao-${modulo.id}`}
                      >
                        <Checkbox 
                          id={`permissao-new-${modulo.id}`}
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData(prev => ({...prev, permissoes: [...prev.permissoes, modulo.id]}))
                            } else {
                              setFormData(prev => ({...prev, permissoes: prev.permissoes.filter(p => p !== modulo.id)}))
                            }
                          }}
                        />
                        <Icon className={`w-4 h-4 ${isChecked ? 'text-teal-600' : 'text-slate-400'}`} />
                        <span className={`text-sm font-medium ${isChecked ? 'text-teal-700' : 'text-slate-600'}`}>
                          {modulo.label}
                        </span>
                      </label>
                    )
                  })}
                </div>
                <p className="text-xs text-slate-500">
                  Selecione os módulos que o usuário poderá acessar
                </p>
              </div>
              
              <DialogFooter>
                <Button type="submit" className="bg-teal-600 hover:bg-teal-700" data-testid="salvar-usuario-btn">
                  Cadastrar Usuário
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog para editar permissões */}
      <Dialog open={permissoesDialogOpen} onOpenChange={setPermissoesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Permissões</DialogTitle>
            <DialogDescription>
              {selectedUser?.nome} ({selectedUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 p-4 bg-slate-50 rounded-lg border">
              {MODULOS.map((modulo) => {
                const Icon = modulo.icon
                const isChecked = selectedPermissoes.includes(modulo.id)
                return (
                  <label 
                    key={modulo.id} 
                    htmlFor={`edit-permissao-${modulo.id}`}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border ${
                      isChecked ? 'bg-teal-50 border-teal-300' : 'bg-white border-slate-200 hover:bg-slate-100'
                    }`}
                    data-testid={`edit-permissao-${modulo.id}`}
                  >
                    <Checkbox 
                      id={`edit-permissao-${modulo.id}`}
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedPermissoes(prev => [...prev, modulo.id])
                        } else {
                          setSelectedPermissoes(prev => prev.filter(p => p !== modulo.id))
                        }
                      }}
                    />
                    <Icon className={`w-4 h-4 ${isChecked ? 'text-teal-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${isChecked ? 'text-teal-700' : 'text-slate-600'}`}>
                      {modulo.label}
                    </span>
                  </label>
                )
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermissoesDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSavePermissoes} className="bg-teal-600 hover:bg-teal-700">
              Salvar Permissões
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total de Usuários</p>
                <p className="text-2xl font-semibold text-slate-900">{usuarios.length}</p>
              </div>
              <Users className="w-8 h-8 text-teal-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Administradores</p>
                <p className="text-2xl font-semibold text-blue-600">{usuarios.filter(u => u.is_admin).length}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Usuários</CardTitle>
          <CardDescription>
            Apenas você (administrador) pode cadastrar e gerenciar usuários
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : usuarios.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <Users className="w-12 h-12 mb-4 text-slate-300" />
              <p>Nenhum usuário cadastrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Cargo</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Permissões</TableHead>
                  <TableHead>Cadastrado em</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.map((usuario) => (
                  <TableRow key={usuario.id} className="hover:bg-slate-50" data-testid={`usuario-row-${usuario.id}`}>
                    <TableCell className="font-medium">{usuario.nome}</TableCell>
                    <TableCell>{usuario.email}</TableCell>
                    <TableCell>{usuario.cargo}</TableCell>
                    <TableCell>
                      {usuario.is_admin ? (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Admin
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-slate-50 text-slate-600">
                          <Shield className="w-3 h-3 mr-1" />
                          Usuário
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {usuario.is_admin ? (
                        <span className="text-xs text-blue-600 font-medium">Acesso total</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(usuario.permissoes || []).length === 0 ? (
                            <span className="text-xs text-slate-400">Sem permissões</span>
                          ) : (
                            (usuario.permissoes || []).slice(0, 3).map(p => (
                              <Badge key={p} variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100">
                                {p}
                              </Badge>
                            ))
                          )}
                          {(usuario.permissoes || []).length > 3 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-100">
                              +{usuario.permissoes.length - 3}
                            </Badge>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">{formatDateTime(usuario.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!usuario.is_admin && (
                          <>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-slate-600 hover:text-teal-700 hover:bg-teal-50" 
                              onClick={() => handleEditPermissoes(usuario)}
                              data-testid={`btn-permissoes-${usuario.id}`}
                            >
                              <Settings className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" 
                              onClick={() => handleDelete(usuario.id, usuario.email)}
                              data-testid={`btn-delete-${usuario.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
